import type { ArticleContentBlock, RichTextBlock, RichTextInline } from '@/domain/article'

export type ArticleTocItem = {
  id: string
  label: string
}

const richBlockTypes = new Set(['paragraph', 'heading', 'list', 'quote', 'code', 'image', 'separator'])

export const richInlineText = (children: RichTextInline[] = []): string =>
  children.map((child) => (child.type === 'text' ? child.text : richInlineText(child.children))).join('')

export const isRichTextBlock = (block: ArticleContentBlock): block is RichTextBlock => richBlockTypes.has(block.type)

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)

const uniqueId = (base: string, used: Set<string>) => {
  const fallback = base || 'section'
  let id = fallback
  let index = 2

  while (used.has(id)) {
    id = `${fallback}-${index}`
    index += 1
  }

  used.add(id)
  return id
}

export const buildArticleToc = (blocks: ArticleContentBlock[]) => {
  const used = new Set<string>()
  const headingIds: Record<number, string> = {}
  const toc: ArticleTocItem[] = []

  blocks.forEach((block, index) => {
    if (block.type === 'section') {
      const id = uniqueId(block.id || slugify(block.title), used)
      toc.push({ id, label: block.title })
      headingIds[index] = id
      return
    }

    if (block.type === 'faq' || block.type === 'checklist' || block.type === 'cta') {
      const id = uniqueId(slugify(block.title), used)
      toc.push({ id, label: block.title })
      headingIds[index] = id
      return
    }

    if (block.type === 'video' || block.type === 'podcast') {
      const id = uniqueId(slugify(block.title), used)
      toc.push({ id, label: block.title })
      headingIds[index] = id
      return
    }

    if (block.type === 'caseComparison') {
      const id = uniqueId('before-and-after', used)
      toc.push({ id, label: 'Before and after' })
      headingIds[index] = id
      return
    }

    if (block.type === 'caseDecision') {
      const id = uniqueId(slugify(block.title), used)
      toc.push({ id, label: block.title })
      headingIds[index] = id
      return
    }

    if (block.type === 'caseDecisionMaking') {
      const id = uniqueId(slugify(block.title), used)
      toc.push({ id, label: block.title })
      headingIds[index] = id
      return
    }

    if (block.type === 'caseMetricList') {
      const id = uniqueId(slugify(block.title), used)
      toc.push({ id, label: block.title })
      headingIds[index] = id
      return
    }

    if (block.type === 'applies' && block.title) {
      const id = uniqueId(block.id || slugify(block.title), used)
      toc.push({ id, label: block.title })
      headingIds[index] = id
      return
    }

    if (isRichTextBlock(block) && block.type === 'heading' && (block.level === 2 || block.level === 3)) {
      const label = richInlineText(block.children)
      const id = uniqueId(slugify(label), used)
      toc.push({ id, label })
      headingIds[index] = id
    }
  })

  return { toc, headingIds }
}

const richBlockPlainText = (block: RichTextBlock): string => {
  if (block.type === 'separator') {
    return ''
  }

  if (block.type === 'list') {
    return block.children.map((item) => richInlineText(item.children)).join('\n')
  }

  if ('children' in block && Array.isArray(block.children)) {
    return richInlineText(block.children)
  }

  if (block.type === 'image') {
    return block.image.alternativeText ?? ''
  }

  return ''
}

export const articleBlocksToPlainText = (blocks: ArticleContentBlock[]): string =>
  blocks
    .map((block) => {
      if (isRichTextBlock(block)) return richBlockPlainText(block)
      if (block.type === 'section') return [block.title, articleBlocksToPlainText(block.body)].join('\n')
      if (block.type === 'facts') return block.facts.map((fact) => `${fact.label}: ${fact.value}`).join('\n')
      if (block.type === 'checklist') return [block.title, ...block.items].join('\n')
      if (block.type === 'timeline') return block.items.map((item) => `${item.date} ${item.label} ${item.detail}`).join('\n')
      if (block.type === 'faq') return block.items.map((item) => `${item.title}\n${articleBlocksToPlainText(item.body)}`).join('\n')
      if (block.type === 'callout') return [block.title, articleBlocksToPlainText(block.body)].join('\n')
      if (block.type === 'applies') {
        return [
          block.title,
          block.body ? articleBlocksToPlainText(block.body) : '',
          block.groups.map((group) => [group.title, ...group.items].join('\n')).join('\n'),
        ]
          .filter(Boolean)
          .join('\n')
      }
      if (block.type === 'steps' || block.type === 'workflow') return block.items.join('\n')
      if (block.type === 'table') return [block.headers.join(' '), ...block.rows.map((row) => row.join(' '))].join('\n')
      if (block.type === 'quoteBlock') return [block.quote, block.cite].filter(Boolean).join('\n')
      if (block.type === 'caseComparison') {
        return [block.before.title, block.before.body, ...block.before.items, block.after.title, block.after.body, ...block.after.items]
          .join('\n')
      }
      if (block.type === 'caseDecision') {
        return [
          block.title,
          articleBlocksToPlainText(block.body),
          block.quote,
          block.cite,
          block.concernsTitle,
          ...block.concerns,
        ]
          .filter(Boolean)
          .join('\n')
      }
      if (block.type === 'caseDecisionMaking') {
        return [
          block.title,
          articleBlocksToPlainText(block.body),
          ...block.groups.flatMap((group) => [group.title, ...group.items]),
          block.examplesTitle,
          ...block.examples.flatMap((example) => [example.title, articleBlocksToPlainText(example.body)]),
        ]
          .filter(Boolean)
          .join('\n')
      }
      if (block.type === 'caseMetricList') {
        return [block.title, ...block.metrics.map((metric) => `${metric.label}: ${metric.value}`)].join('\n')
      }
      if (block.type === 'caseLink') return block.label
      if (block.type === 'decision') return block.items.map((item) => `${item.question} ${item.yes} ${item.no}`).join('\n')
      if (block.type === 'imageBlock') return block.caption ?? block.image.alternativeText ?? ''
      if (block.type === 'video' || block.type === 'podcast') return block.title
      if (block.type === 'cta') return [block.title, articleBlocksToPlainText(block.body)].join('\n')
      return ''
    })
    .filter(Boolean)
    .join('\n\n')
